import { useTranslation } from 'helpers/hooks/useTranslation'
import { useRouter } from 'helpers/navigation'
import { getRecommendedSizeVariant } from 'helpers/recommendedSize'
import { useTrackOriginClick } from 'helpers/tracking/hooks/trackOriginClick'
import React, { FC, useEffect, useMemo, useState } from 'react'
import HighlightRow from 'sharedComponents/StoryStream/HighlightRow'
import Stream from 'sharedComponents/StoryStream/Stream'
import StoryTileCTAContext, {
  StoryTileCTAContextValue,
} from 'sharedComponents/StoryTileConnected/StoryTileCTAContext'
import { TranslateFnType } from 'store'
import {
  useLocaleStore,
  useStoryOverviewPageStore,
  useUrlManagerStore,
} from 'store/context'
import styled from 'styled-components'
import Button from 'uiComponents/atoms/Buttons/Button'
import NumberBadge from 'uiComponents/atoms/NumberBadge/NumberBadge'
import QuickFilterSquare, {
  QuickFilterSquareType,
} from 'uiComponents/atoms/QuickFilter/QuickFilterSquare'
import RowSection from 'uiComponents/atoms/Sections/RowSection'
import { HeadingL } from 'uiComponents/atoms/Text/Headings'
import { RowTypes } from 'uiComponents/molecules/TileGrid/RowBuilder'
import DocumentHead from './DocumentHead'
import { useRequestMoreStories } from './helpers'

export type StoryOverviewFilter = (typeof validFilterNames)[number]

export const validFilterNames = [
  'all',
  'trend',
  'guide',
  'occasion',
  'collection',
] as const

const getAvailableFilters = (
  t: TranslateFnType,
  clickHandler: (id: StoryOverviewFilter) => void,
): QuickFilterSquareType[] => {
  return [
    {
      id: 'all',
      title: t('Alle'),
      onClick: () => clickHandler('all'),
      selected: false,
    },
    {
      id: 'trend',
      title: t('Trends'),
      onClick: () => clickHandler('trend'),
      selected: false,
    },
    {
      id: 'guide',
      title: t('Guides'),
      onClick: () => clickHandler('guide'),
      selected: false,
    },
    {
      id: 'occasion',
      title: t('Anlässe'),
      onClick: () => clickHandler('occasion'),
      selected: false,
    },
    {
      id: 'collection',
      title: t('Kollektionen'),
      onClick: () => clickHandler('collection'),
      selected: false,
    },
  ]
}

const Container = styled(RowSection)`
  margin-top: 30px;
`

const LoadMoreStoriesArea = styled.div`
  display: flex;
  justify-content: center;
  padding: 100px;
`

const HeadlineWrapper = styled('div')`
  display: flex;
  align-items: center;
  margin-top: 41px;
  margin-bottom: 20px;
`

const Headline = styled(HeadingL)`
  margin-right: 10px;
  margin-bottom: 5px;
`

const QuickFilterBar = styled.ul`
  list-style-type: none;
  margin-bottom: 30px;
  overflow: hidden;

  > li {
    float: left;
    margin-right: 10px;
    text-decoration: none;
  }
`

const StoryOverviewPage: FC<{
  openProductMiniADPDialog: ({
    productId,
    filteredSizeId,
  }: {
    productId: number
    filteredSizeId?: number
  }) => void
}> = ({ openProductMiniADPDialog }) => {
  const [t] = useTranslation()
  const storyOverviewPageStore = useStoryOverviewPageStore()
  const storiesLoaded = useStoryOverviewPageStore(
    store => store.storiesAreLoaded,
  )

  const reachedEndOfStream = useStoryOverviewPageStore(
    store => store.reachedEndOfStream,
  )

  const storyCount = useStoryOverviewPageStore(
    store => store.totalAvailableStories,
  )
  const hrefLang = useLocaleStore(store => store.hrefLang)
  const hrefLangLocale = useMemo(() => hrefLang, [])

  const { requestMoreStories, loading } = useRequestMoreStories()

  const urlManagerStore = useUrlManagerStore()
  const { navigateClient, navigateClientWithParams } = useRouter()
  const params = urlManagerStore.params

  const initallySelectedFilter: StoryOverviewFilter =
    params.contentTypes &&
    validFilterNames.includes(params.contentTypes as StoryOverviewFilter)
      ? (params.contentTypes as StoryOverviewFilter)
      : 'all'

  const [selectedFilter, setSelectedFilter] = useState<StoryOverviewFilter>(
    initallySelectedFilter,
  )

  useEffect(() => {
    setSelectedFilter(initallySelectedFilter)
  }, [initallySelectedFilter])

  const ctaContextValue = useMemo<StoryTileCTAContextValue>(
    () => ({
      onProductClick: product => {
        const recommendedSizeVariant = getRecommendedSizeVariant(
          product.variants,
        )
        const variantId = recommendedSizeVariant?.id
          ? Number(recommendedSizeVariant?.id)
          : undefined

        openProductMiniADPDialog({
          productId: Number(product.id),
          filteredSizeId: variantId,
        })
      },
    }),
    [openProductMiniADPDialog],
  )

  const onLoadMoreStories = async () => {
    requestMoreStories()
  }

  const onFilterSelect = (filter: StoryOverviewFilter) => {
    setSelectedFilter(filter)
    if (filter === 'all') {
      navigateClient(urlManagerStore.pathname)()
      return
    }

    navigateClientWithParams(urlManagerStore.pathname)({
      ...params,
      contentTypes: filter,
    })()
  }

  const availableFilters = getAvailableFilters(t, onFilterSelect)

  const currentFilter = availableFilters.find(
    filter => filter.id === selectedFilter,
  )

  const headlineText =
    currentFilter?.id !== 'all' && currentFilter?.title
      ? currentFilter?.title
      : t('Alle Stories')
  const trackOriginClick = useTrackOriginClick()
  return (
    <Container>
      <DocumentHead />
      <HeadlineWrapper>
        <Headline as="h1" data-testid={`AllStories_${currentFilter?.id}`}>
          {headlineText}
        </Headline>
        <NumberBadge type={'disabled'} data-testid={'count'}>
          {storyCount.toLocaleString(hrefLangLocale)}
        </NumberBadge>
      </HeadlineWrapper>
      <QuickFilterBar data-testid={'StoryQuickfilterWrapper'}>
        {availableFilters.map(filter => {
          return (
            <li key={filter.id}>
              <QuickFilterSquare
                id={filter.id}
                title={filter.title}
                selected={filter.id === currentFilter?.id}
                onClick={e => {
                  trackOriginClick('story_filters')
                  // @ts-ignore
                  filter.onClick(e)
                }}
              />
            </li>
          )
        })}
      </QuickFilterBar>
      <StoryTileCTAContext.Provider value={ctaContextValue}>
        <Stream
          loading={!storiesLoaded}
          rowBuilder={storyOverviewPageStore.rowBuilder}
          rowComponentMap={{
            [RowTypes.HighlightRow]: HighlightRow,
          }}
        />
      </StoryTileCTAContext.Provider>
      <LoadMoreStoriesArea>
        {!reachedEndOfStream && (
          <Button
            onClick={onLoadMoreStories}
            disabled={loading}
            data-testid="LoadMoreButton"
          >
            {t('Weitere Stories laden')}
          </Button>
        )}
      </LoadMoreStoriesArea>
    </Container>
  )
}

export default StoryOverviewPage
